/* eslint-disable no-nested-ternary */
/* eslint-disable react/forbid-prop-types */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FiUser } from 'react-icons/fi';
import subYears from 'date-fns/subYears';
import AppTextInput from '../../../../shared/components/app-text-input/AppTextInput';
import AppSelect from '../../../../shared/components/app-select/AppSelect';
import AppDatePicker from '../../../../shared/components/app-date-picker/AppDatePicker';
import {
  PASSENGER_TITLE_OPTIONS,
  OCCUPATION_OPTIONS,
  VISA_PAX_TYPES,
} from '../../constants/onlineVisaConstants';
import {
  getNewDate,
  getAPIFormattedDate,
} from '../../../../shared/helper-utils/appUtils';
import { calculateAge } from '../../utils/onlineVisaUtils';
import './TravelerDetails.scss';

const PAX_LABEL = {
  [VISA_PAX_TYPES.ADULT]: 'Adult',
  [VISA_PAX_TYPES.CHILD]: 'Child',
  [VISA_PAX_TYPES.INFANT]: 'Infant',
};

/**
 * DOB allowed range for a given pax.
 *  - Adult: 12y+  -> minDate = null, maxDate = today - 12y
 *  - Child: pinned to the age the user selected during search (exact year)
 *  - Infant: pinned to the age the user selected during search (exact year)
 *
 * The child/infant restriction is computed off `travelDate` when provided
 * (falls back to today) so the eligible window matches the booking window.
 */
const getDobRange = (pax, travelDate) => {
  const ref = travelDate ? getNewDate(travelDate) : new Date();
  if (pax.type === VISA_PAX_TYPES.ADULT) {
    return {
      minDate: null,
      maxDate: subYears(ref, 12),
    };
  }
  if (pax.type === VISA_PAX_TYPES.CHILD) {
    const age = Number(pax.age);
    if (!Number.isFinite(age)) {
      return { minDate: subYears(ref, 12), maxDate: subYears(ref, 2) };
    }
    return {
      minDate: subYears(ref, age + 1),
      maxDate: subYears(ref, age),
    };
  }
  if (pax.type === VISA_PAX_TYPES.INFANT) {
    const age = Number(pax.age);
    if (!Number.isFinite(age)) {
      return { minDate: subYears(ref, 2), maxDate: ref };
    }
    return {
      minDate: subYears(ref, age + 1),
      maxDate: age === 0 ? ref : subYears(ref, age),
    };
  }
  return { minDate: null, maxDate: null };
};

function FieldWrap({ children, error, errorText }) {
  return (
    <div className="traveler-details__field">
      {children}
      {error && errorText && (
        <small className="traveler-details__err">{errorText}</small>
      )}
    </div>
  );
}

FieldWrap.propTypes = {
  children: PropTypes.node.isRequired,
  error: PropTypes.bool,
  errorText: PropTypes.string,
};

FieldWrap.defaultProps = {
  error: false,
  errorText: '',
};

function TravelerCard({
  pax,
  index,
  onChange,
  showErrors,
  travelDate,
  returnDate,
  duplicatePassportNums,
}) {
  const range = useMemo(() => getDobRange(pax, travelDate), [pax, travelDate]);

  const setField = (field, value) => {
    onChange(index, { ...pax, [field]: value });
  };

  const setDob = (date) => {
    const iso = getAPIFormattedDate(date);
    const age = calculateAge(date);
    onChange(index, { ...pax, dateOfBirth: iso, age });
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const passportIssueMax = new Date();

  const passportNum = (pax.passportNumber || '').trim().toUpperCase();
  const isPassportDuplicate =
    passportNum.length > 0 && duplicatePassportNums.has(passportNum);

  // Validate passport dates
  const travelDateObj = travelDate
    ? getNewDate(travelDate) || new Date(travelDate)
    : null;
  const endTravelDate = returnDate
    ? getNewDate(returnDate) || new Date(returnDate)
    : travelDateObj;

  const passportExpiryMin =
    endTravelDate && endTravelDate >= today ? endTravelDate : today;

  const issueDate = pax.passportIssueDate
    ? getNewDate(pax.passportIssueDate) || new Date(pax.passportIssueDate)
    : null;
  const expiryDate = pax.passportExpiry
    ? getNewDate(pax.passportExpiry) || new Date(pax.passportExpiry)
    : null;

  const passportIssueBeforeTravelDate =
    !issueDate || !travelDateObj || issueDate < travelDateObj;
  const passportValidThroughTrip =
    !expiryDate || !endTravelDate || expiryDate >= endTravelDate;

  const err = {
    title: showErrors && !pax.title,
    firstName: showErrors && !pax.firstName?.trim(),
    lastName: showErrors && !pax.lastName?.trim(),
    dob: showErrors && !pax.dateOfBirth,
    passport:
      showErrors && (!pax.passportNumber?.trim() || isPassportDuplicate),
    passportIssueDate:
      showErrors && (!pax.passportIssueDate || !passportIssueBeforeTravelDate),
    passportExpiry:
      showErrors && (!pax.passportExpiry || !passportValidThroughTrip),
    occupation: showErrors && !pax.occupation,
  };

  const passportErrText = err.passport
    ? isPassportDuplicate
      ? 'Duplicate passport number — each traveller must have a unique passport'
      : 'Passport number required'
    : '';

  const passportIssueDateErrText = err.passportIssueDate
    ? !pax.passportIssueDate
      ? 'Issue date required'
      : 'Issue date must be before the travel date'
    : '';

  const passportExpiryErrText = err.passportExpiry
    ? !pax.passportExpiry
      ? 'Expiry required'
      : returnDate
      ? 'Passport must be valid through the return date'
      : 'Passport must be valid through the travel date'
    : '';

  return (
    <div className="traveler-details__card">
      <div className="traveler-details__card-head">
        <span className="traveler-details__badge">
          <FiUser />
          {PAX_LABEL[pax.type]} {pax.seatIndex}
        </span>
        {pax.primary && (
          <span className="traveler-details__primary-pill">Lead Passenger</span>
        )}
      </div>

      <div className="traveler-details__grid">
        {/* Title — only AppSelect renders the error to avoid double message */}
        <FieldWrap error={err.title}>
          <AppSelect
            name="title"
            label="Title"
            required
            placeholder=""
            isClearable={false}
            options={PASSENGER_TITLE_OPTIONS}
            selectedValue={pax.title || ''}
            onSelect={(opt) => setField('title', opt ? opt.value : '')}
            errorText={err.title ? 'Title required' : ''}
          />
        </FieldWrap>

        <FieldWrap error={err.firstName} errorText="">
          <AppTextInput
            name="firstName"
            label="First Name"
            required
            value={pax.firstName || ''}
            onChange={(e) => setField('firstName', e.target.value)}
            errorText={err.firstName ? 'First name required' : ''}
          />
        </FieldWrap>

        <FieldWrap error={err.lastName} errorText="">
          <AppTextInput
            name="lastName"
            label="Last Name"
            required
            value={pax.lastName || ''}
            onChange={(e) => setField('lastName', e.target.value)}
            errorText={err.lastName ? 'Last name required' : ''}
          />
        </FieldWrap>

        <FieldWrap error={err.dob} errorText="DOB required">
          <AppDatePicker
            name="dateOfBirth"
            label="Date of Birth"
            required
            customClassName={`position-relative flight-booking-picker ${
              err.dob ? 'app-error-text-border' : ''
            }`}
            className="app-date-picker"
            labelClassName="booking-date-label"
            placeholder=""
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            minDate={range.minDate}
            maxDate={range.maxDate}
            selectedDate={pax.dateOfBirth ? getNewDate(pax.dateOfBirth) : ''}
            onChange={setDob}
          />
        </FieldWrap>

        <FieldWrap>
          <AppTextInput
            name="age"
            label="Age"
            disabled
            value={pax.age === '' || pax.age == null ? '' : String(pax.age)}
            onChange={() => {}}
          />
        </FieldWrap>

        <FieldWrap error={err.passport} errorText="">
          <AppTextInput
            name="passportNumber"
            label="Passport Number"
            required
            value={pax.passportNumber || ''}
            onChange={(e) =>
              setField(
                'passportNumber',
                String(e.target.value || '')
                  .replace(/[^A-Za-z0-9]/g, '')
                  .toUpperCase(),
              )
            }
            errorText={passportErrText}
          />
        </FieldWrap>

        <FieldWrap
          error={err.passportIssueDate}
          errorText={passportIssueDateErrText}
        >
          <AppDatePicker
            name="passportIssueDate"
            label="Passport Issue Date"
            required
            customClassName={`position-relative flight-booking-picker ${
              err.passportIssueDate ? 'app-error-text-border' : ''
            }`}
            placeholder=""
            className="app-date-picker"
            labelClassName="booking-date-label"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            maxDate={passportIssueMax}
            selectedDate={
              pax.passportIssueDate ? getNewDate(pax.passportIssueDate) : ''
            }
            onChange={(date) =>
              setField('passportIssueDate', getAPIFormattedDate(date))
            }
          />
        </FieldWrap>

        <FieldWrap error={err.passportExpiry} errorText={passportExpiryErrText}>
          <AppDatePicker
            name="passportExpiry"
            label="Passport Expiry"
            required
            customClassName={`position-relative flight-booking-picker ${
              err.passportExpiry ? 'app-error-text-border' : ''
            }`}
            className="app-date-picker"
            labelClassName="booking-date-label"
            placeholder="DD/MM/YYYY"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            minDate={passportExpiryMin}
            selectedDate={
              pax.passportExpiry ? getNewDate(pax.passportExpiry) : ''
            }
            onChange={(date) =>
              setField('passportExpiry', getAPIFormattedDate(date))
            }
            onChangeRaw={(event) => event.preventDefault()}
            onPaste={(event) => event.preventDefault()}
            onKeyDown={(event) => {
              if (event.key !== 'Tab') event.preventDefault();
            }}
          />
        </FieldWrap>

        <FieldWrap error={err.occupation}>
          <AppSelect
            name="occupation"
            label="Occupation"
            required
            placeholder=""
            isClearable={false}
            options={OCCUPATION_OPTIONS}
            selectedValue={pax.occupation || ''}
            onSelect={(opt) => setField('occupation', opt ? opt.value : '')}
            errorText={err.occupation ? 'Please select an occupation.' : ''}
          />
        </FieldWrap>
      </div>
    </div>
  );
}

TravelerCard.propTypes = {
  pax: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  showErrors: PropTypes.bool.isRequired,
  travelDate: PropTypes.any,
  returnDate: PropTypes.any,
  duplicatePassportNums: PropTypes.instanceOf(Set),
};

TravelerCard.defaultProps = {
  travelDate: '',
  returnDate: '',
  duplicatePassportNums: new Set(),
};

function TravelerDetails({
  travelers,
  onChange,
  showErrors,
  travelDate,
  returnDate,
}) {
  const duplicatePassportNums = useMemo(() => {
    const seen = new Set();
    const dupes = new Set();
    travelers.forEach((p) => {
      const num = (p.passportNumber || '').trim().toUpperCase();
      if (num) {
        if (seen.has(num)) dupes.add(num);
        else seen.add(num);
      }
    });
    return dupes;
  }, [travelers]);

  return (
    <section className="traveler-details">
      <header className="traveler-details__head">
        <h2 className="traveler-details__title">Traveller Details</h2>
        <p className="traveler-details__sub">
          Enter details exactly as they appear on your passport.
        </p>
      </header>

      <div className="traveler-details__list">
        {travelers.map((pax, idx) => (
          <TravelerCard
            key={`${pax.type}-${pax.seatIndex}`}
            pax={pax}
            index={idx}
            onChange={onChange}
            showErrors={showErrors}
            travelDate={travelDate}
            returnDate={returnDate}
            duplicatePassportNums={duplicatePassportNums}
          />
        ))}
      </div>
    </section>
  );
}

TravelerDetails.propTypes = {
  travelers: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  showErrors: PropTypes.bool,
  travelDate: PropTypes.any,
  returnDate: PropTypes.any,
};

TravelerDetails.defaultProps = {
  showErrors: false,
  travelDate: '',
  returnDate: '',
};

export default TravelerDetails;
